<template>
  <rpm-layout>
    <div class="flex justify-between items-center bg-white rounded-lg p-2 mb-2 space-x-1">
      <div class="flex space-x-2 items-center">
        <v-autocomplete
            v-model="client"
            :items="availableClients"
            item-text="name"
            item-value="id"
            label="Client"
            outlined
            hide-details
            style="width: 250px;"
        ></v-autocomplete>
      </div>
      <div class="flex space-x-2 items-center">
        <v-autocomplete
            v-if="isHealthCoachManager"
            v-model="careManager"
            :items="careManagers"
            :item-text="item => `${item.first_name} ${item.last_name}`"
            item-value="id"
            label="Care Manager"
            outlined
            hide-details
            style="width: 250px;"
        ></v-autocomplete>
      </div>

      <div class="flex space-x-2 items-center">
        <select v-model="timePeriod" class="border rounded px-3 py-1.5 text-sm pr-8">
          <option value="daily">Daily</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>
      <div class="flex space-x-2 items-center">
        <v-menu v-model="startMenu" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" small color="primary">
              {{ dateFrom | dateNonLocal }}
            </v-btn>
          </template>
          <v-date-picker v-model="dateFrom" @input="startMenu = false"/>
        </v-menu>
      </div>
      <div class="flex space-x-2 items-center">
        <v-menu v-model="endMenu" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" small color="primary">
              {{ dateTo | dateNonLocal }}
            </v-btn>
          </template>
          <v-date-picker v-model="dateTo" @input="endMenu = false"/>
        </v-menu>
      </div>
      <div class="flex space-x-2 items-center">
        <el-button type="info" @click="refresh">Refresh</el-button>
      </div>
    </div>

    <div class="relative overflow-x-auto sm:rounded-lg bg-white p-4 mb-4">
      <div class="flex items-center mb-4 space-x-3">
        <span class="font-semibold">CPT Task Time</span>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
          </template>
          <span>
            Minutes: Shows the average task time per CPT code.<br/>
            Completion: Shows the average completion percentage per CPT code.<br/>
            Can filter by patient or current health coach.<br/>

            <em>Note: If CareManager filter is selected "All Patients" will show all data based on assigned task user ID. However, not every patient will show up in Patient filter if you're not currently a Care Manager for that patient.</em><br/><br/>

            <strong>CCM Codes (Chronic Care Management):</strong><br/>
            99437: Add-on code for each additional 30 minutes of chronic care management services personally provided by a physician or qualified health care professional (beyond the first 30 minutes under 99491) for a patient with two or more chronic conditions.<br/>
            99439: Add-on code for each additional 20 minutes of clinical staff time directed by a physician or qualified health care professional for non-complex chronic care management services (beyond the initial 20 minutes under 99490) for a patient with two or more chronic conditions.<br/>
            99490: Base code for at least 20 minutes of non-face-to-face clinical staff time directed by a physician or qualified health care professional for non-complex chronic care management of a patient with two or more chronic conditions expected to last at least 12 months.<br/>
            99491: Base code for the first 30 minutes of chronic care management services personally performed by a physician or qualified health care professional (not clinical staff) for a patient with two or more chronic conditions.<br/><br/>

            <strong>RPM Codes (Remote Physiologic Monitoring):</strong><br/>
            99453: Code for initial setup and patient education on the use of remote physiologic monitoring device(s).<br/>
            99445: New code (2026) for supply of device(s) and collection/transmission of remote physiologic monitoring data for 2–15 days in a 30-day period.<br/>
            99454: Code for supply of device(s) and collection/transmission of remote physiologic monitoring data for at least 16 days in a 30-day period.<br/>
            99457: Base code for the first 20 minutes of remote physiologic monitoring treatment management services (including interactive communication) by clinical staff, physician, or qualified health care professional in a calendar month.<br/>
            99458: Add-on code for each additional 20 minutes of remote physiologic monitoring treatment management services (beyond the first 20 minutes under 99457) in a calendar month.<br/>
            99470: New code (2026) for 10–19 minutes of remote physiologic monitoring treatment management services (including interactive communication) by clinical staff, physician, or qualified health care professional in a calendar month.<br/><br/>

            <strong>APCM Codes (Advanced Primary Care Management):</strong><br/>
            G0556: Monthly code for advanced primary care management services (provided by clinical staff under physician direction) for a patient with one or fewer chronic conditions.<br/>
            G0557: Monthly code for advanced primary care management services (provided by clinical staff under physician direction) for a patient with two or more chronic conditions expected to last at least 12 months.<br/>
            G0558: Monthly code for advanced primary care management services (provided by clinical staff under physician direction) for a Qualified Medicare Beneficiary patient with two or more chronic conditions.
          </span>
        </v-tooltip>
      </div>

      <div v-if="taskTimeLoading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-else-if="taskTimeChartData" class="chart-container">
        <chart :data="taskTimeChartData" :options="taskTimeChartOptions"/>
      </div>
      <div v-else class="text-center py-8 text-gray-500">
        No task time data available for the selected period
      </div>
    </div>
  </rpm-layout>
</template>

<script>
import moment from "moment-timezone";
import RpmLayout from "../../layouts/RpmLayout.vue";
import PatientMixin from "@/mixins/Patient";
import Chart from "../../components/Chart.vue";
import {mapState} from "vuex";

export default {
  mixins: [PatientMixin],
  components: { RpmLayout, Chart },
  data() {
    return {
      loading: true,
      client: null,
      careManager: null,
      timePeriod: 'daily',
      dateFrom: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      dateTo: moment().format('YYYY-MM-DD'),
      startMenu: false,
      endMenu: false,
      taskTimeData: null,
      taskTimeLoading: false,
      viewMode: 'completion',
    };
  },
  created: async function () {
    await this.$auth.getTokenSilently();
    Promise.all([
      !this.$store.state.patients.authUser.id ? this.$store.dispatch('getAuthUser') : null,
      !this.$store.state.patients.providers.length ? this.$store.dispatch('getProviders') : null,
      !this.$store.state.patients.patients.length ? this.$store.dispatch('getPatients') : null,
    ]).finally(() => {
      if (this.availableClients.length > 0) {
        this.client = this.availableClients[0].id;
      } else {
        this.loading = false;
      }
    });
  },
  computed: {
    ...mapState(["patients"]),
    authUser() {
      return this.$store.state.patients.authUser;
    },
    isHealthCoachManager() {
      return this.authUser && this.authUser.behaviors && this.authUser.behaviors.includes('is_health_coach_manager');
    },
    careManagers() {
      const managers = this.$store.state.patients.providers.filter(
          (p) => p.behaviors.includes("is_health_coach") && !p.disabled
      );
      return [
        { first_name: "All", last_name: "Care Managers", id: null },
        ...managers,
      ];
    },
    isHealthCoach() {
      return this.authUser && this.authUser.behaviors && this.authUser.behaviors.includes('is_health_coach');
    },
    availableClients() {
      if (!this.authUser || !this.authUser.available_clients) return [];
      return this.authUser.available_clients;
    },
    taskTimeChartData() {
      if (!this.taskTimeData || !this.taskTimeData.length) return null;

      const filteredData = this.taskTimeData;
      const rawDates = [...new Set(filteredData.map(item => item.date))].sort();
      const dateFilter = this.$options.filters.dateOrdinal;
      const labels = rawDates.map(d => dateFilter(d));

      const getCptPrefix = (code) => {
        const codeStr = String(code);
        if (['99458', '99457', '99445', '99454', '99470'].includes(codeStr)) return 'RPM';
        if (['99439', '99490'].includes(codeStr)) return 'CCM';
        return 'CPT';
      };

      const uniqueCptCodes = [...new Set(filteredData.map(item => item.cpt_code))];
      const cptCodeDetails = uniqueCptCodes.map(code => ({
        code,
        label: `${getCptPrefix(code)} ${code}`
      })).sort((a, b) => a.label.localeCompare(b.label));

      const colors = ['#42A5F5', '#66BB6A', '#FFA726', '#EF5350'];
      const field = this.viewMode === 'completion' ? 'completion' : 'minutes';

      const datasets = cptCodeDetails.map((cpt, index) => {
        const dataForCode = filteredData.filter(item => item.cpt_code === cpt.code);
        const data = rawDates.map(rawDate => {
          const point = dataForCode.find(d => d.date === rawDate);
          return point ? point[field] : null;
        });
        const color = colors[index % colors.length];
        return {
          label: cpt.label,
          data,
          borderColor: color,
          backgroundColor: `${color}33`,
          tension: 0.1,
          fill: false,
        };
      });

      return { labels, datasets };
    },
    taskTimeChartOptions() {
      const isCompletion = this.viewMode === 'completion';
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                min: 0,
                ...(isCompletion ? { suggestedMax: 100 } : {}),
                callback: function (value) {
                  return isCompletion ? value + '%' : value + ' min';
                },
              },
              scaleLabel: {
                display: true,
                labelString: isCompletion ? 'Completion Percentage' : 'Minutes'
              }
            }
          ],
          xAxes: [{
            scaleLabel: {
              display: true,
              labelString: 'Date'
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || '';
              if (label) label += ': ';
              label += isCompletion ? tooltipItem.yLabel + '%' : tooltipItem.yLabel + ' min';
              return label;
            }
          }
        },
        ...(isCompletion ? {
          annotation: {
            annotations: [{
              type: 'line',
              mode: 'horizontal',
              scaleID: 'y-axis-0',
              value: 100,
              borderColor: 'green',
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                enabled: true,
                position: 'right',
                yAdjust: -10,
              }
            }]
          }
        } : {}),
      };
    },
  },
  watch: {
  client() {
    if (this.isHealthCoachManager) {
      this.careManager = null;
    }
    this.loadTaskTimeData();
  },
    careManager() {
      this.loadTaskTimeData();
    },
    timePeriod() {
      this.loadTaskTimeData();
    },
    dateFrom() {
      this.loadTaskTimeData();
    },
    dateTo() {
      this.loadTaskTimeData();
    },
  },
  methods: {
    getPatientCareManagerId(patient) {
      if (!patient) return null;
      if (patient.cache && patient.cache.careTeam && patient.cache.careTeam.care_manager != null) {
        return patient.cache.careTeam.care_manager;
      }
      if (patient.care_manager != null) {
        return patient.care_manager;
      }
      if (patient.care_manager_id != null) {
        return patient.care_manager_id;
      }
      if (patient.cache && patient.cache.care_manager != null) {
        return patient.cache.care_manager;
      }
      return null;
    },
    refresh() {
      this.loadTaskTimeData();
    },
    async loadTaskTimeData() {
      if (!this.client) {
        this.taskTimeLoading = false;
        return;
      }
      this.taskTimeLoading = true;
      try {
        const token = await this.$auth.getTokenSilently();
        const params = {
          token,
          client: this.client,
          time_period: this.timePeriod,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          care_manager_id: this.careManager || -1,
        };
        const response = await this.$http.get(
          `/api/rpm_ccm_dashboard/cpt-task-time-data`,
          { params }
        );
        this.taskTimeData = response.data.data;
      } catch (error) {
        this.$awn.alert('Error loading CPT task time data');
      } finally {
        this.taskTimeLoading = false;
        this.loading = false;
      }
    },
  }
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 450px;
}
</style>
